import { message, Tag } from "antd";
import _ from "lodash";
import { useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { cedisLocale } from "../../helpers/utilities";
import { useQuery } from "react-query";
import { getBillReceipt } from "../../helpers/api";
import { Loader } from "@mantine/core";
import { format } from "date-fns";
import { usePDF } from "react-to-pdf";
import { getUser } from "../../helpers/auth";
import { IconCheck } from "@tabler/icons";
import { BILL_STATE } from "../../helpers/config";

const BillReceipt = ({
  query,
  param = "billId" || "receiptNumber",
  options = {
    enablePrinting: true,
    allowSizeChange: true,
    showOperator: true,
  },
}) => {
  // hooks
  const [size, setSize] = useState(localStorage.getItem("printerSize") || "58");
  const [receipt, setReceipt] = useState({});
  const printArea = useRef();
  const { toPDF, targetRef } = usePDF({
    filename: `rivia-receipt-${receipt?.billNumber || ""}.pdf`,
  });

  // query
  const { data, isFetching } = useQuery({
    queryFn: () => getBillReceipt(param, query),
    queryKey: ["receipt", query],
    cacheTime: 0,
    onSuccess: (data) => {
      // Calculate services total
      const servicesTotal =
        parseFloat(
          data?.items.reduce(
            (total, c) => total + parseFloat(c.rate * c.quantity || 0),
            0
          )
        ) || 0;

      // Calculate charges total
      const chargesTotal =
        parseFloat(
          data?.charges.reduce(
            (total, c) => total + parseFloat(c.value || 0),
            0
          )
        ) || 0;

      // Total amount including both services and charges
      const totalAmount = servicesTotal + chargesTotal;
      const totalDiscount =
        (data?.discount || 0) +
        data?.items?.reduce(
          (total, item) => total + parseFloat(item.discount || 0),
          0
        );

      setReceipt({
        ...data,
        servicesTotal,
        chargesTotal,
        sumAmount: totalAmount,
        discount: parseFloat(totalDiscount).toFixed(1) || 0,
        amountPaid:
          parseFloat(
            data?.payments.reduce(
              (total, c) => total + parseFloat(c.amount || 0),
              0
            )
          ) || 0,
      });
    },
  });

  const balance = parseFloat(
    receipt.sumAmount -
    (parseFloat(receipt.discount) || 0) -
    receipt?.amountPaid
  );

  const isPublished = receipt?.state === BILL_STATE.PUBLISHED;

  // handlers
  const handlePrint = useReactToPrint({
    content: () => printArea.current,
    documentTitle: "receipt " + receipt.receipt_number,
    copyStyles: true,
    bodyClass: "sale-receipt",
  });

  const handleSizeChange = (size) => {
    setSize(size);
    localStorage.setItem("printerSize", size.toString());
  };

  return (
    <>
      <div
        className={`sale-receipt ${size === "58" ? " print-size-58" : " print-size-80"
          }`}>
        {isFetching ? (
          <Loader> loading...</Loader>
        ) : (
          <div ref={targetRef}>
            <div className="receipt-content" ref={printArea}>
              {/* Brand accent */}
              <div className="receipt-brandbar" />

              {/* Letterhead */}
              <div className="receipt-header">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="provider-block">
                    <div className="provider-name">
                      {receipt?.branch?.facility?.facilityName || "—"}
                    </div>
                    <div className="provider-meta">
                      {receipt?.branch?.branchName && (
                        <div>{receipt.branch.branchName}</div>
                      )}
                      {receipt?.branch?.contact && (
                        <div>{receipt.branch.contact}</div>
                      )}
                    </div>
                  </div>
                  <div
                    className={`status-pill ${isPublished ? "is-paid" : "is-draft"
                      }`}>
                    {isPublished ? (
                      <>
                        <IconCheck size={12} strokeWidth={3} /> PAID
                      </>
                    ) : (
                      "DRAFT"
                    )}
                  </div>
                </div>
              </div>

              {/* Draft disclaimer */}
              {!isPublished && (
                <div className="draft-note">
                  This is a draft receipt and does not reflect a completed
                  transaction.
                </div>
              )}

              {/* Amount hero */}
              <div className="amount-hero">
                <div className="amount-label">
                  {isPublished ? "Amount Paid" : "Amount Due"}
                </div>
                <div className="amount-value">
                  <span className="amount-currency">GHS</span>
                  {cedisLocale.format(receipt?.amountPaid || 0)}
                </div>
              </div>

              {/* Meta */}
              <div className="meta-grid">
                <div className="meta-item">
                  <span className="meta-key">Receipt #</span>
                  <span className="meta-val">{receipt?.billNumber || "—"}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-key">
                    {isPublished ? "Payment date" : "Draft date"}
                  </span>
                  <span className="meta-val">
                    {receipt?.createdAt &&
                      format(
                        new Date(receipt?.createdAt),
                        "MMM d, yyyy • h:mm a"
                      )}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-key">Billed to</span>
                  <span className="meta-val">
                    {receipt?.patient?.firstName} {receipt?.patient?.lastName}
                  </span>
                </div>
              </div>

              {/* Line items */}
              <div className="line-table">
                <div className="line-head">
                  <span className="lh-qty">Qty</span>
                  <span className="lh-desc">Description</span>
                  <span className="lh-amt">Amount</span>
                </div>

                {receipt?.items?.map((s) => (
                  <div className="line-row" key={s.serviceId}>
                    <span className="lh-qty">{s.quantity}×</span>
                    <span className="lh-desc">{s.service?.serviceName}</span>
                    <span className="lh-amt">
                      {cedisLocale.format(
                        parseInt(s.quantity) * parseFloat(s.rate).toFixed(1)
                      )}
                    </span>
                  </div>
                ))}

                {!_.isEmpty(receipt?.charges) && (
                  <>
                    <div className="line-subhead">Additional charges</div>
                    {receipt?.charges.map((ch, ind) => (
                      <div className="line-row" key={ind}>
                        <span className="lh-qty" />
                        <span className="lh-desc">{ch.charge?.title}</span>
                        <span className="lh-amt">
                          {cedisLocale.format(parseFloat(ch.value).toFixed(1))}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Summary */}
              <div className="rcpt-summary">
                <div className="sum-row">
                  <span>Subtotal</span>
                  <span>
                    {cedisLocale.format(
                      parseFloat(receipt.sumAmount || 0).toFixed(1)
                    )}
                  </span>
                </div>

                {receipt?.discount > 0 && (
                  <div className="sum-row discount">
                    <span>Discount</span>
                    <span>
                      -{cedisLocale.format(parseFloat(receipt.discount).toFixed(1))}
                    </span>
                  </div>
                )}

                <div className="sum-row">
                  <span>Amount paid</span>
                  <span>
                    {cedisLocale.format(
                      parseFloat(receipt?.amountPaid || 0).toFixed(1)
                    )}
                  </span>
                </div>

                <div
                  className={`sum-row total ${balance > 0 ? "due" : "settled"}`}>
                  <span>Balance</span>
                  <span>
                    {cedisLocale.format(parseFloat(balance || 0).toFixed(1))}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="receipt-footer">
                <div className="thanks">
                  Thank you for choosing{" "}
                  {receipt?.branch?.facility?.facilityName || "us"}.
                </div>
                {options.showOperator && (
                  <div className="foot-line">
                    Served by{" "}
                    {getUser()
                      ? `${getUser()?.staff?.name}`
                      : `${receipt?.user?.staff?.firstName} ${receipt?.user?.staff?.lastName}`}
                  </div>
                )}
                <div className="foot-line">
                  Generated from CareOS on{" "}
                  {format(new Date(), "MM/dd/yy h:mm a")}
                  {receipt?.consultation?.code &&
                    ` • consultation ${receipt?.consultation?.code}`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="p-3">
          <div className="buttons has-addons">
            <button
              onClick={() => {
                message.info("downloading...");
                toPDF();
              }}
              title="Download"
              className="w-50 p-2 button app-btn btn-warning">
              <span className="bi bi-download me-2" />
              Download
            </button>
            {options.enablePrinting && (
              <button
                className="w-50 p-2 button app-btn btn-prim"
                onClick={handlePrint}>
                <span className="bi bi-printer me-2" />
                Print
              </button>
            )}
          </div>

          {options.allowSizeChange && (
            <div className="mt-1">
              <select
                placeholder="select printer size"
                className="input mt- is-small mb-2 w-100"
                value={size}
                onChange={(e) => handleSizeChange(e.target.value)}>
                <option value="58">Paper Size: 58 mm</option>
                <option value="80">Paper Size: 80 mm</option>
                <option value="a4">Paper Size: A4</option>
              </select>
              <Tag color="gold" className="w-100 text-center">
                selection will be remembered
              </Tag>
            </div>
          )}
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .sale-receipt {
          max-width: 460px;
          margin: 0 auto;
        }

        .receipt-content {
          background: #ffffff;
          color: #1f2937;
          font-size: 13px;
          line-height: 1.45;
          border: 1px solid #eef0f3;
          border-radius: 14px;
          overflow: hidden;
        }

        .receipt-brandbar {
          height: 6px;
          background: linear-gradient(90deg, #0991cc 0%, #1d4f9a 100%);
        }

        .receipt-header {
          padding: 16px 20px 14px;
          border-bottom: 1px solid #f0f2f5;
        }

        .provider-name {
          font-size: 1.1rem;
          font-weight: 700;
          color: #111827;
          letter-spacing: -0.01em;
        }

        .provider-meta {
          color: #6b7280;
          font-size: 0.78rem;
          margin-top: 3px;
        }

        .status-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 0.66rem;
          font-weight: 800;
          letter-spacing: 0.09em;
          padding: 4px 10px;
          border-radius: 999px;
          border: 1px solid transparent;
          white-space: nowrap;
        }

        .status-pill.is-paid {
          color: #067a53;
          background: #e7f8f0;
          border-color: #b6ebd5;
        }

        .status-pill.is-draft {
          color: #92600a;
          background: #fef4e2;
          border-color: #f7dca6;
        }

        .draft-note {
          margin: 12px 20px 0;
          padding: 8px 12px;
          font-size: 0.74rem;
          color: #92600a;
          background: #fef4e2;
          border: 1px dashed #f0c473;
          border-radius: 8px;
        }

        .amount-hero {
          text-align: center;
          padding: 18px 20px 8px;
        }

        .amount-label {
          text-transform: uppercase;
          letter-spacing: 0.09em;
          font-size: 0.68rem;
          color: #6b7280;
          font-weight: 700;
        }

        .amount-value {
          font-size: 2.15rem;
          font-weight: 800;
          color: #111827;
          line-height: 1.1;
          margin-top: 4px;
        }

        .amount-currency {
          font-size: 0.95rem;
          font-weight: 600;
          color: #6b7280;
          margin-right: 6px;
          vertical-align: middle;
        }

        .meta-grid {
          padding: 8px 20px 4px;
        }

        .meta-item {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 5px 0;
          border-bottom: 1px dashed #eef0f3;
        }

        .meta-item:last-child {
          border-bottom: none;
        }

        .meta-key {
          color: #6b7280;
          font-size: 0.78rem;
        }

        .meta-val {
          color: #111827;
          font-weight: 600;
          font-size: 0.82rem;
          text-align: right;
        }

        .line-table {
          padding: 10px 20px 4px;
        }

        .line-head {
          display: flex;
          align-items: center;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-size: 0.64rem;
          font-weight: 700;
          color: #9097a1;
          padding-bottom: 6px;
          border-bottom: 1.5px solid #111827;
        }

        .line-subhead {
          font-size: 0.68rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #9097a1;
          font-weight: 700;
          padding: 10px 0 2px;
        }

        .line-row {
          display: flex;
          align-items: flex-start;
          padding: 7px 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .line-row:last-child {
          border-bottom: none;
        }

        .lh-qty {
          width: 36px;
          flex: 0 0 36px;
          color: #6b7280;
          font-weight: 600;
        }

        .lh-desc {
          flex: 1 1 auto;
          padding-right: 8px;
          color: #1f2937;
        }

        .lh-amt {
          flex: 0 0 auto;
          text-align: right;
          font-variant-numeric: tabular-nums;
          font-weight: 600;
          color: #111827;
          white-space: nowrap;
        }

        .rcpt-summary {
          margin: 12px 20px 0;
          background: #f9fafb;
          border: 1px solid #eef0f3;
          border-radius: 10px;
          padding: 10px 14px;
        }

        .sum-row {
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
          font-size: 0.85rem;
          color: #4b5563;
        }

        .sum-row span:last-child {
          font-variant-numeric: tabular-nums;
          font-weight: 600;
          color: #111827;
        }

        .sum-row.discount span {
          color: #067a53;
        }

        .sum-row.total {
          margin-top: 6px;
          padding-top: 10px;
          border-top: 1.5px dashed #d7dbe0;
          font-size: 1rem;
          font-weight: 800;
          color: #111827;
        }

        .sum-row.total span {
          font-weight: 800;
        }

        .sum-row.total.due span:last-child {
          color: #dc2626;
        }

        .sum-row.total.settled span:last-child {
          color: #067a53;
        }

        .receipt-footer {
          padding: 16px 20px 20px;
          margin-top: 10px;
          border-top: 1px solid #f0f2f5;
          text-align: center;
        }

        .thanks {
          font-size: 0.82rem;
          color: #374151;
          font-weight: 600;
          margin-bottom: 6px;
        }

        .foot-line {
          font-size: 0.72rem;
          color: #9097a1;
        }

        @media print {
          .sale-receipt {
            max-width: none;
            box-shadow: none;
          }

          .receipt-content {
            border: none;
            border-radius: 0;
            font-size: 12px;
          }

          .receipt-brandbar {
            display: none;
          }

          .status-pill,
          .rcpt-summary,
          .draft-note {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .print-size-58 .receipt-header,
          .print-size-58 .amount-hero,
          .print-size-58 .meta-grid,
          .print-size-58 .line-table,
          .print-size-58 .receipt-footer {
            padding-left: 6px;
            padding-right: 6px;
          }

          .print-size-58 .rcpt-summary,
          .print-size-58 .draft-note {
            margin-left: 6px;
            margin-right: 6px;
          }
        }
      `}</style>
    </>
  );
};

export default BillReceipt;
